import { createTheme, THEME_ACT_ET } from "@actinc/dls";
import { alertClasses } from "@mui/material/Alert";

const partialTheme = createTheme(THEME_ACT_ET, {
  palette: {
    background: {
      alert: "#d9f3ff",
      tooltip: "#fafafa",
    },
    secondary: {
      main: "#6f6f6f",
    },
    info: {
      contrastText: "#ffffff",
      dark: "#042e60",
    },
    warning: {
      contrastText: "#000000",
      main: "#f9db7c",
      dark: "#9e5f00",
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const theme = createTheme(partialTheme, {
  components: {
    MuiAlert: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        icon: {
          paddingBottom: 0,
          paddingTop: 0,
        },
        message: {
          paddingBottom: 0,
          paddingTop: 0,
        },
        root: {
          alignItems: "center",
          borderRadius: 4,
          display: "flex",
          justifyContent: "start",
          marginBottom: "1.6rem",
          [`&.${alertClasses.filled}.${alertClasses.colorError}`]: {
            backgroundColor: partialTheme.palette.error.main,
            color: partialTheme.palette.error.contrastText,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorInfo}`]: {
            backgroundColor: partialTheme.palette.info.dark,
            color: partialTheme.palette.info.contrastText,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorSuccess}`]: {
            backgroundColor: partialTheme.palette.success.dark,
            color: partialTheme.palette.success.contrastText,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorWarning}`]: {
            backgroundColor: partialTheme.palette.warning.main,
            color: partialTheme.palette.warning.contrastText,
          },
          [`&.${alertClasses.outlined}.${alertClasses.colorError}`]: {
            backgroundColor: partialTheme.palette.background.alert,
            border: `0.1rem solid ${partialTheme.palette.divider}`,
            borderLeft: `0.5em solid ${partialTheme.palette.error.dark}`,
            borderRadius: 8,
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            color: partialTheme.palette.text.secondary,
            "& .MuiAlert-icon": {
              color: partialTheme.palette.error.dark,
            },
          },
          [`&.${alertClasses.outlined}.${alertClasses.colorInfo}`]: {
            backgroundColor: partialTheme.palette.background.alert,
            border: `0.1rem solid ${partialTheme.palette.divider}`,
            borderLeft: `0.5em solid ${partialTheme.palette.info.dark}`,
            borderRadius: 8,
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            color: partialTheme.palette.text.secondary,
            "& .MuiAlert-icon": {
              color: partialTheme.palette.info.dark,
            },
          },
          [`&.${alertClasses.outlined}.${alertClasses.colorSuccess}`]: {
            backgroundColor: partialTheme.palette.background.alert,
            border: `0.1rem solid ${partialTheme.palette.divider}`,
            borderLeft: `0.5em solid ${partialTheme.palette.success.dark}`,
            borderRadius: 8,
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            color: partialTheme.palette.text.secondary,
            "& .MuiAlert-icon": {
              color: partialTheme.palette.success.dark,
            },
          },
          [`&.${alertClasses.outlined}.${alertClasses.colorWarning}`]: {
            backgroundColor: partialTheme.palette.background.alert,
            border: `0.1rem solid ${partialTheme.palette.divider}`,
            borderLeft: `0.5em solid ${partialTheme.palette.warning.dark}`,
            borderRadius: 8,
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
            color: partialTheme.palette.text.secondary,
            "& .MuiAlert-icon": {
              color: partialTheme.palette.warning.dark,
            },
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        li: {
          "& p": {
            color: partialTheme.palette.secondary.main,
          },
        },
        root: {
          marginBottom: "3.2rem",
          marginTop: "1rem",
        },
        separator: {
          color: partialTheme.palette.divider,
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: typeof jest !== "undefined" },
    },
    MuiCheckbox: {
      defaultProps: { disableRipple: typeof jest !== "undefined" },
      styleOverrides: {
        colorPrimary: {
          "&.Mui-checked": {
            color: partialTheme.palette.info.dark,
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        columnHeaderHeight: 50,
        disableColumnFilter: true,
        rowHeight: 50,
      },
      styleOverrides: {
        columnHeader: {
          backgroundColor: "#f2f2f2",
        },
        columnHeaderTitle: {
          color: `${partialTheme.palette.text.secondary} !important`,
          fontSize: "1.4rem",
          fontWeight: "bold",
          marginLeft: 4,
        },
        root: {
          "& .MuiDataGrid-filler": {
            backgroundColor: "#f2f2f2",
          },
        },
      },
    },
    MuiDateField: {
      defaultProps: {
        margin: "dense",
        size: "small",
        variant: "outlined",
      },
    },
    MuiFormControl: {
      defaultProps: {
        margin: "dense",
        size: "small",
        variant: "outlined",
      },
      styleOverrides: {
        marginDense: {
          margin: "0",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: partialTheme.palette.text.secondary,
          fontSize: "1.4rem",
          lineHeight: "2.4rem",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: partialTheme.palette.secondary.light,
          "&.Mui-error": {
            color: partialTheme.palette.error.main,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: partialTheme.palette.text.secondary,
          fontSize: "1.4rem",
          fontWeight: "bold",
          lineHeight: "2.4rem",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: partialTheme.palette.text.secondary,
          fontSize: "1.4rem",
          fontWeight: "bold",
          lineHeight: "2.4rem",
          marginBottom: "0.4rem",
          "&.Mui-focused": {
            color: partialTheme.palette.text.secondary,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: "Montserrat",
          fontSize: "1.4rem",
          lineHeight: "2.4rem",
        },
      },
    },
    MuiRadio: {
      defaultProps: { disableRipple: typeof jest !== "undefined" },
      styleOverrides: {
        colorPrimary: {
          "&.Mui-checked": {
            color: partialTheme.palette.info.dark,
          },
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      },
      styleOverrides: {
        root: {
          marginTop: "6.4rem",
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          [`&.MuiStepConnector-vertical`]: {
            marginLeft: 10,
            [`& .MuiStepConnector-line`]: {
              minHeight: 12,
            },
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: "1.2rem",
        },
        vertical: {
          padding: "2px 0",
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          listStyleType: "none",
          paddingLeft: 0,
        },
      },
    },
    MuiTab: {
      defaultProps: { disableRipple: typeof jest !== "undefined" },
      styleOverrides: {
        root: {
          backgroundColor: partialTheme.palette.background.paper,
          borderLeft: `0.1rem solid ${partialTheme.palette.divider}`,
          borderRight: `0.1rem solid ${partialTheme.palette.divider}`,
          borderTop: `0.1rem solid ${partialTheme.palette.divider}`,
          borderTopLeftRadius: partialTheme.shape.borderRadius,
          borderTopRightRadius: partialTheme.shape.borderRadius,
          color: partialTheme.palette.primary.main,
          marginRight: "0.1rem",
          padding: "0.9rem 1.2rem",
          "&.Mui-selected": {
            backgroundColor: partialTheme.palette.background.tooltip,
            color: partialTheme.palette.text.secondary,
          },
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: partialTheme.palette.background.tooltip,
          "&:before": {
            border: `0.1rem solid ${partialTheme.palette.divider}`,
          },
        },
        tooltip: {
          backgroundColor: partialTheme.palette.background.tooltip,
          border: `0.1rem solid ${partialTheme.palette.divider}`,
          borderRadius: partialTheme.shape.borderRadius,
          boxShadow: "0 0 0.5rem ${partialTheme.palette.divider}",
          color: partialTheme.palette.text.secondary,
          fontSize: "14px",
          lineHeight: "2.4rem",
          maxWidth: "450px",
          padding: "0.5rem 1.6rem",
          position: "relative",
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        subtitle2: {
          fontSize: "1.8rem",
        },
      },
    },
  },
});

export default theme;
